import { measureText, SelectOption } from '@chronosphereio/chrono-ui';
import { DEFAULT_ALL_VALUE } from '@chronosphereio/core';
import { useMemo } from 'react';
import { VariableOption } from '../../../core';

export const INPUT_MIN_WIDTH = 120;
export const INPUT_MAX_WIDTH = 350;
const CHECKBOX_WIDTH = 28;
const MENU_ITEM_PADDING = 32;

/**
 * Hook that contains some common logic for single/multi select inputs for template variables.
 */
export function useTemplateVariableOptions(allowAllValue: boolean, allowMultiple: boolean, options?: VariableOption[]) {
  // Make it easier to find selected options by using a Map to index them by key
  const { optionsByKey, popperMinWidth } = useMemo(() => {
    let minWidth = INPUT_MIN_WIDTH;
    const map = new Map<string, SelectOption>();

    if (options === undefined) {
      return { optionsByKey: map, popperMinWidth: minWidth };
    }

    // Map remembers insertion order so add All value first if enabled
    if (allowAllValue) {
      map.set(DEFAULT_ALL_VALUE, { key: DEFAULT_ALL_VALUE, label: 'All' });
    }

    const length = options.length;
    for (let index = 0; index < length; index++) {
      const option = options[index];
      if (option) {
        map.set(option.value, { key: option.value, label: option.label });

        // Only check the width of the first 100 options to improve performance
        if (index < 100) {
          // Set the min width to the "width" of the longest option
          let optionWidth = getOptionWidth(option.label);
          if (allowMultiple) {
            optionWidth += CHECKBOX_WIDTH;
          }
          if (optionWidth > minWidth) {
            minWidth = optionWidth;
          }
        }
      }
    }

    return { optionsByKey: map, popperMinWidth: minWidth };
  }, [options, allowAllValue, allowMultiple]);

  // Control expects an array of SelectOptions, so convert the Map
  const selectOptions = useMemo(() => Array.from(optionsByKey.values()), [optionsByKey]);

  // Boolean to indicate if options have been loaded.
  // We do not count "All" as a loaded option, so if there is only one option (All) then we have not loaded options.
  const hasLoadedOptions = useMemo(() => {
    if (!allowAllValue) {
      return selectOptions.length > 0;
    } else {
      return selectOptions.length > 1;
    }
  }, [selectOptions, allowAllValue]);

  return { optionsByKey, popperMinWidth, selectOptions, hasLoadedOptions };
}

const PX_PER_CHARACTER = 8;

function getOptionWidth(optionLabel: string) {
  const textWidth = measureText(optionLabel, '14px Inter')?.width;
  if (textWidth !== undefined) {
    return Math.min(textWidth + MENU_ITEM_PADDING, INPUT_MAX_WIDTH);
  }
  // fallback in case context is null, though this should rarely happen
  return Math.min(PX_PER_CHARACTER * optionLabel.length, INPUT_MAX_WIDTH);
}
